// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import _ from 'lodash';
import { toast } from 'sonner';

import useOrganizationStore from '@/store/organizationStore';
import useEmailTemplateStore from '@/store/emailTemplateStore';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { PageHeaderSkeleton, ListSkeleton } from '@/components/Page/PageSkeletons';
import { ArrowLeft, Save } from 'lucide-react';

const TEMPLATE_META: Record<string, { title: string; description: string; variables: string[] }> = {
  team_registration_confirmation: {
    title: 'Team Registration Confirmation',
    description: 'Sent to the team manager after they submit players to register their team.',
    variables: [
      'teamName', 'firstName', 'lastName', 'managerEmail',
      'organizationName', 'leagueName', 'contestName', 'divisionName',
      'playerCount', 'playerNames',
    ],
  },
  payment_confirmation: {
    title: 'Payment Confirmation',
    description: 'Sent to the payer after a successful Stripe checkout.',
    variables: [
      'teamName', 'managerEmail',
      'organizationName', 'leagueName',
      'amount', 'paymentDate',
    ],
  },
};

export default function EmailTemplateEditor() {
  const { templateKey } = useParams<{ templateKey: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { leagueName = '' } = useOutletContext() as { leagueName?: string };
  const { organizations } = useOrganizationStore();

  const { getTemplateByKey: getTemplateByKeyFn, upsertTemplate: upsertTemplateFn } = useEmailTemplateStore((s: any) => ({
    getTemplateByKey: s.getTemplateByKey,
    upsertTemplate: s.upsertTemplate,
  }));
  const getTemplateByKey = useMemo(() => getTemplateByKeyFn(getToken), [getTemplateByKeyFn, getToken]);
  const upsertTemplate = useMemo(() => upsertTemplateFn(getToken), [upsertTemplateFn, getToken]);

  const organization = organizations?.find((l: any) => l.name === leagueName);
  const meta = TEMPLATE_META[templateKey || ''] || TEMPLATE_META.team_registration_confirmation;

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const lastFocused = useRef<'subject' | 'body'>('body');

  useEffect(() => {
    if (!organization?._id || !templateKey) return;
    setLoading(true);
    getTemplateByKey(organization._id, templateKey)
      .then((tpl: any) => {
        if (tpl) {
          setSubject(tpl.subject || '');
          setBody(tpl.body || '');
          setEnabled(tpl.enabled !== false);
        }
      })
      .finally(() => setLoading(false));
  }, [organization?._id, templateKey]);

  const insertVariable = (varName: string) => {
    const token = `{{${varName}}}`;
    if (lastFocused.current === 'subject' && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? subject.length;
      const end = el.selectionEnd ?? subject.length;
      const next = subject.slice(0, start) + token + subject.slice(end);
      setSubject(next);
      requestAnimationFrame(() => {
        el.focus();
        const cursor = start + token.length;
        el.setSelectionRange(cursor, cursor);
      });
    } else if (bodyRef.current) {
      const el = bodyRef.current;
      const start = el.selectionStart ?? body.length;
      const end = el.selectionEnd ?? body.length;
      const next = body.slice(0, start) + token + body.slice(end);
      setBody(next);
      requestAnimationFrame(() => {
        el.focus();
        const cursor = start + token.length;
        el.setSelectionRange(cursor, cursor);
      });
    }
  };

  const handleSave = async () => {
    if (!organization?._id || !templateKey) return;
    setIsSaving(true);
    try {
      await upsertTemplate(organization._id, templateKey, { subject, body, enabled });
      toast.success('Template saved');
    } catch {
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton />
        <ListSkeleton rows={4} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/email-templates')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          className="flex-1"
          title={meta.title}
          description={meta.description}
          actions={(
            <Button onClick={handleSave} disabled={isSaving}>
              <Save className="h-4 w-4 mr-1" />
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          )}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Available variables</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {meta.variables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertVariable(v)}
              className="hover:opacity-80"
              title={`Insert {{${v}}}`}
            >
              <Badge variant="outline" className="cursor-pointer font-mono">{`{{${v}}}`}</Badge>
            </button>
          ))}
          <p className="w-full text-xs text-muted-foreground mt-2">
            Click a variable to insert it at the cursor in either Subject or Body.
            Uses Handlebars syntax: <code>{'{{variable}}'}</code>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded border-input"
            />
            <span className="text-sm">Enabled (template will be sent alongside the built-in email)</span>
          </label>

          <div>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              ref={subjectRef}
              value={subject}
              onFocus={() => (lastFocused.current = 'subject')}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Your team {{teamName}} is registered!"
            />
          </div>

          <div>
            <Label htmlFor="body">Body (HTML)</Label>
            <Textarea
              id="body"
              ref={bodyRef}
              value={body}
              onFocus={() => (lastFocused.current = 'body')}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              placeholder={'<p>Hi {{firstName}},</p>\n<p>Your team {{teamName}} is registered for {{leagueName}}.</p>'}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              HTML allowed. Use double-brace variables like <code>{'{{teamName}}'}</code> to interpolate runtime values.
            </p>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
